import { lazy, Suspense, useState } from "react"
import PropTypes from "prop-types"

import { updateChannelScript, RunStepTest } from "./api.js"

import displayFlash from "partners/scripts/display_flash"

import "./adapter_script.scss"

const MonacoEditor = lazy(() => import("shared/components/monaco_editor"))

export default function AdapterScript({ service }) {
  const outputDefaultRequest = `{
  "fields": {},
  "limit": 1
}`
  const [script, updateScript] = useState(service.script)
  const [buttonDisabled, updateButtonDisabled] = useState(false)
  const [bodyRequest, updateBodyRequest] = useState(outputDefaultRequest)
  const [outputRequest, updateOutputRequest] = useState()

  const onSaveScript = e => {
    e.preventDefault()
    updateButtonDisabled(true)
    updateChannelScript(service.slug, script)
      .then(resp => {
        if (
          resp.errors?.length > 0 ||
          resp.data?.updateChannelScript?.errors.length > 0
        ) {
          console.error(resp.errors, resp.data?.updateChannelScript?.errors)
          displayFlash("Script not updated! Please try again later.", "error")
        } else {
          updateScript(resp.data.updateChannelScript.script)
          displayFlash("Script updated!")
        }
      })
      .finally(() => {
        updateButtonDisabled(false)
      })
  }

  const onTestScript = e => {
    e.preventDefault()
    updateButtonDisabled(true)
    updateOutputRequest("")

    const [step_module_name, step_field_slug] = document
      .querySelector("select[name='step_module_name']")
      .value.split(".")
    const operation = document
      .querySelector("select[name='operation']")
      .value.toUpperCase()
    let bodyProvided

    try {
      bodyProvided = JSON.parse(
        document.querySelector("textarea[name='step_body']").value
      )
    } catch (error) {
      updateButtonDisabled(false)
      updateOutputRequest(
        "Fail to JSON.parse request body. Check value provided."
      )
      return
    }

    // Reduce issues sending wrong data
    const stepBody = {
      fields: bodyProvided.fields,
      limit: bodyProvided.limit,
      cursor: bodyProvided.cursor,
      value: bodyProvided.value,
      values: bodyProvided.values,
      trigger_identity: bodyProvided.trigger_identity,
    }

    RunStepTest(
      service.slug,
      step_module_name,
      step_field_slug || "",
      operation,
      stepBody
    )
      .then(resp => {
        updateOutputRequest(`
Request:
channel_module_name: ${service.slug},
step_module_name: ${step_module_name},
step_field_slug: ${step_field_slug || ""},
operation: ${operation},
step_body: ${JSON.stringify(stepBody)}

Response:
${JSON.stringify(resp)}
        `)
      })
      .finally(() => {
        updateButtonDisabled(false)
      })
  }

  const endpointOptions = () => {
    const steps = {
      triggers: "trigger_fields",
      queries: "query_fields",
      actions: "trigger_fields",
    }
    let step_module_name
    let step_field_slug
    let field_key
    const options = []

    Object.keys(steps).forEach(stepType => {
      service[stepType].forEach(step => {
        // First level: TQA
        step_module_name = step.module_name
        options.push(
          <option
            value={step_module_name}
            key={`${stepType}.${step_module_name}`}
            data-roles={
              stepType === "triggers"
                ? "run,contextual_validation,delete_trigger_identity"
                : "run,contextual_validation"
            }
          >
            /ifttt/v1/{stepType}/{step_module_name}
          </option>
        )

        // Second level: TQA fields
        field_key = steps[stepType]
        step[field_key]?.forEach(step_field => {
          step_field_slug = step_field.slug
          options.push(
            <option
              value={`${step_module_name}.${step_field_slug}`}
              key={`${stepType}.${step_module_name}.${step_field_slug}`}
              data-roles="dropdown,validate"
              disabled // Only first option level is enabled because default operation is run.
            >
              /ifttt/v1/{stepType}/{step_module_name}/field/{step_field_slug}
            </option>
          )
        })
      })
    })

    return options
  }

  const onOperationChange = operationValue => {
    const endpointSelect = document.querySelector(
      "select[name='step_module_name']"
    )

    endpointSelect.querySelectorAll("option").forEach(option => {
      option.disabled = !option.dataset["roles"].includes(operationValue)
    })

    endpointSelect.value = endpointSelect.querySelector(
      "option:not([disabled])"
    ).value
  }

  return (
    <>
      <form className="form" styleName="script-editor">
        <div className="field">
          <div className="label">
            <label>Channel script</label>
          </div>
          <div className="input">
            <Suspense fallback="Loading...">
              <MonacoEditor
                positionAbsolute={false}
                defaultValue={script}
                onChange={updateScript}
                location_type="adapter_script"
              />
            </Suspense>
          </div>
        </div>

        <button
          className="button-secondary"
          styleName="script-cta"
          onClick={onSaveScript}
          disabled={buttonDisabled}
        >
          Update script
        </button>
      </form>

      <form className="form" styleName="script-run">
        <div className="field">
          <div className="label">
            <label>Operation</label>
          </div>
        </div>
        <div className="input">
          <select
            name="operation"
            onChange={e => onOperationChange(e.target.value)}
          >
            <option value="run">RUN</option>
            <option value="dropdown">DROPDOWN</option>
            <option value="validate">VALIDATE</option>
            <option value="contextual_validation">CONTEXTUAL_VALIDATION</option>
            <option value="delete_trigger_identity">
              DELETE_TRIGGER_IDENTITY
            </option>
          </select>
        </div>

        <div className="field">
          <div className="label">
            <label>Request endpoint</label>
          </div>
        </div>
        <div className="input">
          <select name="step_module_name" onChange={() => {}}>
            {endpointOptions()}
          </select>
        </div>

        <div className="field">
          <div className="label">
            <label>Request body</label>
          </div>
        </div>
        <div className="input">
          <textarea
            required="required"
            rows="6"
            name="step_body"
            value={bodyRequest}
            onChange={e => updateBodyRequest(e.target.value)}
          ></textarea>
        </div>

        <div styleName="actions">
          <a
            target="_blank"
            rel="noreferrer"
            styleName="logs-link"
            href={`https://logs.ifttt.com/_plugin/kibana/app/discover#/view/61a0d970-a855-11eb-a1f7-d14751133036?_a=(query:(language:kuery,query:'message_json.mdc.channel:${service.slug}'))`}
          >
            See logs
          </a>

          <button
            className="button-secondary"
            styleName="script-cta"
            onClick={onTestScript}
            disabled={buttonDisabled}
          >
            Run script
          </button>
        </div>

        {outputRequest && (
          <pre styleName="output-container">
            <code id="outputContainer">{outputRequest}</code>
          </pre>
        )}
      </form>
    </>
  )
}

AdapterScript.propTypes = {
  service: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    script: PropTypes.string,
    trigger: PropTypes.shape({
      module_name: PropTypes.string,
      trigger_fields: PropTypes.shape({
        slug: PropTypes.string,
      }),
    }),
    query: PropTypes.shape({
      module_name: PropTypes.string,
      query_fields: PropTypes.shape({
        slug: PropTypes.string,
      }),
    }),
    action: PropTypes.shape({
      module_name: PropTypes.string,
      action_fields: PropTypes.shape({
        slug: PropTypes.string,
      }),
    }),
  }),
}
